'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { debounce } from 'lodash';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { ProductsGrid } from '@eliadmin/core/src/components/product/products-grid';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { buildProductsQuery } from '@eliadmin/core/src/utils/product-utils';
import { LuSearch } from 'react-icons/lu';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export function CustomSearch({ categoryId }: { categoryId: number }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const locale = useLocaleStore(s => s.currentLocale);
  const authToken = useCustomerStore(s => s.me?.authToken);
  const isCustomerInitialized = useCustomerStore(s => s.isInitialized);

  const productsQuery = useMemo(
    () =>
      buildProductsQuery({
        categories: [categoryId],
      }),
    [categoryId],
  );

  // Fetch products function
  const fetchCategoryProducts = useCallback(
    async (term: string = '') => {
      if (!isCustomerInitialized) return;
      setIsLoading(true);
      try {
        const response = await fetchProducts({
          query: {
            ...(term ? { search: term } : {}), // Only add search param if term exists
            results: 12, // Limit results, adjust as needed
            ...productsQuery,
          },
          locale,
          authToken,
        });
        setProducts(response.data || []);
      } catch (error) {
        console.error('Error fetching products:', error);
        setProducts([]);
      } finally {
        setIsLoading(false);
      }
    },
    [locale, authToken, isCustomerInitialized, productsQuery],
  );

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((term: string) => {
      fetchCategoryProducts(term);
    }, 500),
    [fetchCategoryProducts],
  );

  // Load all products initially
  useEffect(() => {
    if (!isCustomerInitialized) return;
    fetchCategoryProducts(''); // Fetch all products when component mounts
  }, [fetchCategoryProducts, isCustomerInitialized]);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const { t } = useTranslations();

  return (
    <div className="flex w-full flex-col gap-6">
      <Input
        before={<LuSearch className="ml-6 h-5 w-5 scale-x-[-1] text-[#B2B2B2] " />}
        type="text"
        placeholder={t('text2022')}
        value={searchTerm}
        onChange={handleSearchChange}
        className={{
          main: 'w-full pl-14',
          wrapper: 'w-full',
        }}
      />
      {!isLoading && products.length === 0 && searchTerm && (
        <div className="text-center text-muted-foreground">
          {t('text2023')} {searchTerm}
        </div>
      )}

      {!isLoading && products.length === 0 && !searchTerm && (
        <div className="text-center text-muted-foreground">{t('text2024')}</div>
      )}

      {products.length > 0 && (
        <ProductsGrid
          products={products}
          className={{
            wrapper: cn(
              'grid w-full grid-cols-2 @lg:grid-cols-2 @2xl:grid-cols-3',
              'ea-category-products__grid',
            ),
          }}
        />
      )}
    </div>
  );
}
